import { push, replace } from "connected-react-router";
import isNumber from "lodash-es/isNumber";
import { normalize, schema } from "normalizr";
import { parse } from "query-string";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import { BoxedHashtags } from "@/mk2/apps/blogs/components/HashtagsBox";
import {
    BLOGS_FEED_FETCH_TRIGGER,
    BLOGS_FEED_SCROLLTO_TRIGGER,
    BlogsFeedFetchNormalizedResponse,
    BlogsFeedFetchTriggerAction,
    BlogsFeedScrollToTriggerAction,
    blogsFeedFetchApi,
    blogsFeedScrollToTrigger,
} from "@/mk2/apps/blogs/containers/BlogsFeed/BlogsFeed.actions";
import {
    BlogsFeedCategory,
    ScrollToPosition,
} from "@/mk2/apps/blogs/containers/BlogsFeed/BlogsFeedPage";
import { blogsFeedWithCategoryUrl, blogsUrl } from "@/mk2/apps/blogs/urls";
import { ComplaintEntity, ComplaintSchema } from "@/mk2/containers/Complaint/Complaint.schemas";
import { pageHeaderFetchFlaggedCountsTrigger } from "@/mk2/containers/PageHeader/PageHeader.action";
import { XHRAction, XHRGetError, handleXHRGetErrorSaga, normalizeError } from "@/mk2/helpers/api";
import { mergeDenormalized } from "@/mk2/helpers/denormalized";
import { scrollElementTo } from "@/mk2/helpers/scrolling";
import { removeEmpty, removePage1, url } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import { getRequestDeviceMobile } from "@/mk2/reducers";
import { BlogPostEntity, BlogPostSchema } from "@/mk2/schemas";
import { blogs_api_feed_get_url } from "@/mk/urls/functions";

const logger = getLogger("blogs/BlogsFeed.sagas");

interface ApiResponseFetchBlogsFeed {
    body: {
        entities: BlogsFeedFetchNormalizedResponse["entities"];
        result: {
            complaints: ComplaintEntity[];
        };
        brandDaysPostId: number;
        hasComplaints: boolean;
        hashtags: BoxedHashtags;
        page: number;
        posts: BlogPostEntity[];
        postsSubtreeComplaintList: { [postId: number]: ComplaintEntity[] };
    };
}

function* fetchBlogsFeed({
    currentUrl,
    numPostsBeforeLoad,
    pageRef,
    category,
    postType,
    query,
    xhr,
}: BlogsFeedFetchTriggerAction & XHRAction) {
    const qs = parse(currentUrl.search);

    if (postType === null) {
        const gotoUrl =
            category === BlogsFeedCategory.ALL
                ? blogsUrl
                : url(blogsFeedWithCategoryUrl, { category });
        yield put(push(gotoUrl));
        return;
    }

    if (!pageRef) {
        // if pageRef is 0, negative or invalid string, we redirect us to page=1
        const gotoUrl = url(blogsUrl, {}, removeEmpty(removePage1({ ...qs, page: 1 })));
        yield put(replace(gotoUrl));
        return;
    } else if (pageRef && qs.page && qs.page !== pageRef.toString()) {
        // if we succeed parsing incorrect string (e.g. page=3a), then redir to correct page (e.g. page=3)
        const gotoUrl = url(blogsUrl, {}, removeEmpty(removePage1({ ...qs, page: pageRef })));
        yield put(replace(gotoUrl));
        return;
    }

    if (numPostsBeforeLoad > 0 && qs.scrollto) {
        // if data is already loaded, scroll to bottom immediately
        // so that user does not experience scroll delayed by api call
        yield put(blogsFeedScrollToTrigger(qs.scrollto as ScrollToPosition));
    }
    yield put(blogsFeedFetchApi.request(pageRef, category, postType, query));

    try {
        const response: ApiResponseFetchBlogsFeed = yield call(() =>
            xhr.get(
                blogs_api_feed_get_url(category),
                removeEmpty({
                    page: pageRef,
                    post_type: postType,
                    query,
                }),
            ),
        );

        // ak skusame nahravat zly page number (negativny, vacsi ako pocet stranok, atd.), backend nas redirectne
        if (isNumber(pageRef) && pageRef !== response.body.page) {
            throw new Error("Wrong page");
        }

        const normalizedResponse: BlogsFeedFetchNormalizedResponse = normalize(
            {
                postsSubtreeComplaintList: response.body.postsSubtreeComplaintList,
                posts: response.body.posts,
            },
            {
                postsSubtreeComplaintList: new schema.Values([ComplaintSchema]),
                posts: [BlogPostSchema],
            },
        );

        // TODO: Remove when all entities will be normalized on server
        const mergedResponse = mergeDenormalized(normalizedResponse, {
            entities: response.body.entities,
            result: response.body.result,
        });

        yield put(
            blogsFeedFetchApi.success(
                pageRef,
                category,
                postType,
                query,
                response.body.page,
                mergedResponse,
                response.body.hasComplaints,
                response.body.hashtags,
                response.body.brandDaysPostId,
            ),
        );

        if (qs.page === "1") {
            // remove ?page=1 from url
            const gotoUrl = url(blogsUrl, {}, removeEmpty(removePage1({ ...qs, page: 1 })));
            yield put(replace(gotoUrl));
        }

        if (category === BlogsFeedCategory.FLAGGED) {
            yield put(pageHeaderFetchFlaggedCountsTrigger());
        }

        const numPostsAfterLoad = response.body.posts ? response.body.posts.length : 0;
        if (numPostsBeforeLoad !== numPostsAfterLoad && qs.scrollto) {
            // other count of posts, either first load, or data on server changed, so scroll again
            yield put(blogsFeedScrollToTrigger(qs.scrollto as ScrollToPosition));
        }
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(
            blogsFeedFetchApi.failure(pageRef, category, postType, query, normalizeError(error)),
        );
    }
}

function* scrolltoBlogsFeed({ scrollto }: BlogsFeedScrollToTriggerAction) {
    const isMobile = yield select(getRequestDeviceMobile);
    if (scrollto === ScrollToPosition.LastPost) {
        scrollElementTo("#scrolltoLastHere", "window-bottom", 0);
    } else if (scrollto === ScrollToPosition.FirstPost) {
        scrollElementTo("#scrolltoFirstHere", "window-top", isMobile ? 200 : 45);
    }
}

export default function* root() {
    yield all([
        takeLatest(BLOGS_FEED_FETCH_TRIGGER, fetchBlogsFeed),
        takeLatest(BLOGS_FEED_SCROLLTO_TRIGGER, scrolltoBlogsFeed),
    ]);
}
